// GameIntelligenceCanvas — inline game analysis panel for module workspaces.
// Reads activeGameId / activeGameSport from useOSStore and self-loads game data.
// Replaces the routed GameDetailPage and WNBAGameDetailPage for in-workspace navigation.

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  X, Zap, RefreshCw, AlertTriangle, Brain, DollarSign,
  Users, Activity, TrendingUp, TrendingDown, Minus, PlusCircle, CheckCircle,
} from 'lucide-react';
import { useOSStore } from '../../store/useOSStore';
import { useAppStore } from '../../store/useAppStore';
import { gamesApi, advisorApi, wnbaApi, nbaApi, betsApi } from '../../api/client';
import { ExpandedGameWorkspace } from '../DiamondEdge/ExpandedGameWorkspace';
import { TeamMetricsPanel } from '../CourtEdge/panels/TeamMetricsPanel';
import { RestAnalysisPanel } from '../CourtEdge/panels/RestAnalysisPanel';
import { FormTrendPanel } from '../CourtEdge/panels/FormTrendPanel';
import type { BetRecommendation, LiveGameData, WNBAPlayerProjection, WNBAInjuryReport, NBAPlayerProjection, NBAInjuryReport, Bet, AgentOpinion, AgentName, ParlayLeg } from '../../types';
import type { GameContext } from '../../api/client';

// ── MLB canvas ────────────────────────────────────────────────────────────────

function MLBCanvas({ gamePk, onClose }: { gamePk: number; onClose: () => void }) {
  const { liveGames, recommendations: storeRecs, fetchRecommendations } = useAppStore();
  const [ctx, setCtx] = useState<GameContext | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [recs, setRecs] = useState<BetRecommendation[]>([]);
  const [polledLiveData, setPolledLiveData] = useState<LiveGameData | undefined>(undefined);

  const sseData: LiveGameData | undefined = liveGames[gamePk];
  const liveData = sseData ?? polledLiveData;

  // Seed recs from store
  useEffect(() => {
    const gameRecs = storeRecs.filter((r) => r.gamePk === gamePk);
    if (gameRecs.length > 0) setRecs(gameRecs);
  }, [storeRecs, gamePk]);

  // Load game context
  useEffect(() => {
    setLoading(true);
    setError(null);
    gamesApi
      .getGame(gamePk)
      .then(setCtx)
      .catch(() => setError('Failed to load game data.'))
      .finally(() => setLoading(false));
  }, [gamePk]);

  // Poll live data
  useEffect(() => {
    let cancelled = false;
    const poll = () => {
      gamesApi.getLiveGame(gamePk)
        .then((data) => { if (!cancelled) setPolledLiveData(data); })
        .catch(() => {/* not live */});
    };
    poll();
    const id = setInterval(poll, 10_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [gamePk]);

  const handleAnalyze = async () => {
    setAnalyzing(true);
    try {
      const data = await advisorApi.analyzeGame(gamePk);
      setRecs(data);
      void fetchRecommendations(undefined, true);
    } finally {
      setAnalyzing(false);
    }
  };

  // Auto-analyze if no recs
  useEffect(() => {
    if (!gamePk) return;
    if (storeRecs.some((r) => r.gamePk === gamePk)) return;
    void handleAnalyze();
  }, [gamePk]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="flex flex-col h-full bg-surface-0">
        <CanvasHeader sport="mlb" title="Loading…" onClose={onClose} analyzing={false} onAnalyze={() => void 0} />
        <div className="flex-1 flex items-center justify-center">
          <RefreshCw className="w-5 h-5 text-gray-600 animate-spin" />
        </div>
      </div>
    );
  }

  if (error || !ctx) {
    return (
      <div className="flex flex-col h-full bg-surface-0">
        <CanvasHeader sport="mlb" title="Error" onClose={onClose} analyzing={false} onAnalyze={() => void 0} />
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="flex items-start gap-3 bg-loss/10 border border-loss/20 rounded-lg p-4 max-w-sm">
            <AlertTriangle className="w-4 h-4 text-loss flex-shrink-0 mt-0.5" />
            <p className="text-xs text-loss/80">{error ?? 'Game not found.'}</p>
          </div>
        </div>
      </div>
    );
  }

  const { game, odds, weather, dataQuality, awayStandings, homeStandings } = ctx;

  return (
    <div className="flex flex-col h-full overflow-hidden bg-surface-0">
      <CanvasHeader
        sport="mlb"
        title={`${game.awayTeam.name} @ ${game.homeTeam.name}`}
        subtitle={game.venue.name}
        status={game.status.abstractGameState}
        onClose={onClose}
        analyzing={analyzing}
        onAnalyze={handleAnalyze}
      />
      <div className="flex-1 overflow-hidden">
        <ExpandedGameWorkspace
          game={game}
          liveData={liveData}
          odds={odds}
          weather={weather}
          dataQuality={dataQuality}
          awayStandings={awayStandings}
          homeStandings={homeStandings}
          recommendations={recs}
          analyzing={analyzing}
          onAnalyze={handleAnalyze}
        />
      </div>
    </div>
  );
}

// ── WNBA canvas ───────────────────────────────────────────────────────────────

type WNBATab =
  | 'overview'
  | 'markets'
  | 'metrics'
  | 'rest'
  | 'form'
  | 'recommendations'
  | 'evidence'
  | 'bets'
  | 'props'
  | 'injuries';

const WNBA_TABS: { key: WNBATab; label: string }[] = [
  { key: 'overview',        label: 'Overview' },
  { key: 'markets',         label: 'Markets' },
  { key: 'metrics',         label: 'Team Metrics' },
  { key: 'rest',            label: 'Rest Analysis' },
  { key: 'form',            label: 'Form Trends' },
  { key: 'recommendations', label: 'Picks' },
  { key: 'evidence',        label: 'Evidence' },
  { key: 'bets',            label: 'Bets' },
  { key: 'props',           label: 'Props' },
  { key: 'injuries',        label: 'Injuries' },
];

function formatOddsAmerican(n?: number): string {
  if (n === undefined) return '—';
  return n > 0 ? `+${n}` : `${n}`;
}

function WNBARecCard({ rec }: { rec: BetRecommendation }) {
  const isStrong = rec.decisionStatus === 'STRONG_BET';
  const isQualified = rec.decisionStatus === 'QUALIFIED_BET';
  return (
    <div className={clsx(
      'rounded-lg border p-4',
      isStrong ? 'border-win/40 bg-win/5' : isQualified ? 'border-brand/30 bg-brand/5' : 'border-surface-4 bg-surface-2',
    )}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <span className="text-xs text-gray-500 uppercase tracking-wide">{rec.market}</span>
          <p className="text-sm font-semibold text-gray-100 mt-0.5">{rec.selection}</p>
        </div>
        <div className="text-right flex-shrink-0">
          <p className={clsx('text-xs font-bold', isStrong ? 'text-win' : isQualified ? 'text-brand-light' : 'text-gray-500')}>
            {rec.decisionStatus?.replace('_', ' ')}
          </p>
          {rec.edge !== undefined && (
            <p className="text-xs text-gray-500 mt-0.5">Edge: {(rec.edge * 100).toFixed(1)}%</p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
        {rec.modelProbability !== undefined && <span>Model: {(rec.modelProbability * 100).toFixed(1)}%</span>}
        {rec.confidenceLevel && <span>Conf: {rec.confidenceLevel}</span>}
        {rec.odds && <span>Odds: {rec.odds > 0 ? '+' : ''}{rec.odds}</span>}
      </div>
    </div>
  );
}

// ── WNBA sub-tab components ───────────────────────────────────────────────────

const AGENT_LABELS: Partial<Record<AgentName, string>> = {
  market:          'Market',
  matchup:         'Team Metrics',
  form:            'Form',
  context:         'Rest & Fatigue',
  mlModel:         'ML Model',
  validation:      'Validation',
  portfolio:       'Portfolio',
  closingLineValue: 'CLV',
  risk:            'Risk',
};

function AgentOpinionCard({ name, opinion }: { name: AgentName; opinion: AgentOpinion }) {
  const scorePct = Math.round(opinion.score * 100);
  const isPositive = opinion.score > 0.55;
  const isNegative = opinion.score < 0.45;
  return (
    <div className="rounded-lg border border-surface-4 bg-surface-2 p-3 space-y-2.5">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className={clsx(
            'text-[10px] font-bold px-1.5 py-0.5 rounded border',
            isPositive ? 'bg-win/10 border-win/30 text-win' :
            isNegative ? 'bg-loss/10 border-loss/30 text-loss' :
            'bg-surface-3 border-surface-4 text-gray-500',
          )}>
            {AGENT_LABELS[name] ?? name}
          </span>
          <span className="text-[10px] text-gray-600">{opinion.signal}</span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="w-16 h-1.5 rounded-full bg-surface-3 overflow-hidden">
            <div
              className={clsx('h-full rounded-full transition-all', isPositive ? 'bg-win' : isNegative ? 'bg-loss' : 'bg-gray-600')}
              style={{ width: `${scorePct}%` }}
            />
          </div>
          <span className="text-[10px] font-mono tabular-nums text-gray-500 w-7 text-right">{scorePct}</span>
        </div>
      </div>

      {opinion.reasoning.length > 0 && (
        <ul className="space-y-1">
          {opinion.reasoning.slice(0, 4).map((r, i) => (
            <li key={i} className="flex items-start gap-1.5 text-xs text-gray-500">
              {r.direction === 'positive' ? (
                <TrendingUp className="w-3 h-3 text-win flex-shrink-0 mt-0.5" />
              ) : r.direction === 'negative' ? (
                <TrendingDown className="w-3 h-3 text-loss flex-shrink-0 mt-0.5" />
              ) : (
                <Minus className="w-3 h-3 text-gray-600 flex-shrink-0 mt-0.5" />
              )}
              {r.text}
            </li>
          ))}
        </ul>
      )}

      {opinion.dataWarnings.length > 0 && (
        <div className="rounded border border-warn/20 bg-warn/5 px-2 py-1.5">
          {opinion.dataWarnings.map((w, i) => (
            <p key={i} className="text-[10px] text-warn/80">⚠ {w}</p>
          ))}
        </div>
      )}
    </div>
  );
}

function WNBAEvidenceTab({ recommendations }: { recommendations: BetRecommendation[] }) {
  if (recommendations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-2 text-center">
        <Brain className="w-6 h-6 text-gray-700" />
        <p className="text-sm text-gray-600">Run analysis to see agent reasoning.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl">
      {recommendations.map((rec) => (
        <div key={rec.id} className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-600 uppercase tracking-wide">{rec.market}</p>
              <p className="text-sm font-semibold text-gray-100">{rec.selection}</p>
            </div>
            <div className="text-right">
              <p className={clsx(
                'text-xs font-bold',
                rec.decisionStatus === 'STRONG_BET' ? 'text-win' :
                rec.decisionStatus === 'QUALIFIED_BET' ? 'text-brand-light' : 'text-gray-500',
              )}>
                {rec.decisionStatus?.replace('_', ' ')}
              </p>
              {rec.edge !== undefined && (
                <p className="text-xs text-gray-600 mt-0.5">Edge: +{(rec.edge * 100).toFixed(1)}%</p>
              )}
            </div>
          </div>
          {rec.agentOpinions && (
            <div className="space-y-2">
              {(Object.entries(rec.agentOpinions) as [AgentName, AgentOpinion][])
                .filter(([, op]) => op.signal !== 'neutral' || op.reasoning.length > 0)
                .map(([agentName, opinion]) => (
                  <AgentOpinionCard key={agentName} name={agentName} opinion={opinion} />
                ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function WNBABetsTab({
  game,
  recommendations,
  gameBets,
  onPlaceBet,
  onSettleBet,
}: {
  game: { eventPk: string; gameDate: string; awayTeam: { name: string }; homeTeam: { name: string } };
  recommendations: BetRecommendation[];
  gameBets: Bet[];
  onPlaceBet: (data: Parameters<typeof betsApi.place>[0]) => Promise<Bet>;
  onSettleBet: (id: string, result: 'won' | 'lost' | 'push' | 'void', closingOdds?: number) => Promise<void>;
}) {
  const [placing, setPlacing] = useState<string | null>(null);
  const [settling, setSettling] = useState<string | null>(null);
  const { addLegToSlip, parlaySlip } = useAppStore();

  const activeBets = gameBets.filter((b) => b.status === 'placed' || b.status === 'live');
  const settledBets = gameBets.filter((b) =>
    b.status === 'settled' || b.status === 'won' || b.status === 'lost' || b.status === 'push' || b.status === 'void',
  );

  return (
    <div className="space-y-5 max-w-2xl">
      {/* Actionable picks */}
      {recommendations.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Qualified Picks</p>
          {recommendations.map((rec) => (
            <div key={rec.id} className={clsx(
              'rounded-lg border p-3',
              rec.decisionStatus === 'STRONG_BET'
                ? 'border-win/30 bg-win/5'
                : 'border-brand/20 bg-brand/5',
            )}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">{rec.market}</p>
                  <p className="text-sm font-semibold text-gray-100 mt-0.5">{rec.selection}</p>
                  <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                    <span>Odds: {rec.odds > 0 ? '+' : ''}{rec.odds}</span>
                    {rec.edge !== undefined && <span>Edge: +{(rec.edge * 100).toFixed(1)}%</span>}
                    {rec.confidenceLevel && <span className="capitalize">{rec.confidenceLevel} conf.</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {/* Add to Parlay */}
                  {(() => {
                    const inSlip = parlaySlip.some((l) => l.recommendationId === rec.id);
                    return (
                      <button
                        disabled={inSlip}
                        onClick={() => {
                          const leg: ParlayLeg = {
                            id: crypto.randomUUID(),
                            eventPk: game.eventPk,
                            leagueId: rec.leagueId ?? 'wnba',
                            gameDate: game.gameDate,
                            awayTeam: game.awayTeam.name,
                            homeTeam: game.homeTeam.name,
                            market: rec.market,
                            selection: rec.selection,
                            odds: rec.odds,
                            impliedProbability: rec.impliedProbability,
                            modelProbability: rec.modelProbability,
                            edge: rec.edge,
                            confidence: rec.confidence,
                            recommendationId: rec.id,
                          };
                          addLegToSlip(leg);
                        }}
                        title={inSlip ? 'Already in parlay slip' : 'Add to parlay slip'}
                        className={clsx(
                          'flex items-center gap-1 px-2 py-1.5 rounded-md text-[10px] font-semibold border transition-colors',
                          inSlip
                            ? 'bg-win/15 border-win/30 text-win cursor-default'
                            : 'border-surface-4 text-gray-500 hover:text-brand-light hover:border-brand/30',
                        )}
                      >
                        {inSlip ? <CheckCircle className="w-3 h-3" /> : <PlusCircle className="w-3 h-3" />}
                        {inSlip ? 'In slip' : 'Parlay'}
                      </button>
                    );
                  })()}

                  <button
                    disabled={placing === rec.id}
                    onClick={async () => {
                      setPlacing(rec.id);
                      try {
                        await onPlaceBet({
                          eventPk: game.eventPk,
                          gameDate: game.gameDate,
                          awayTeam: game.awayTeam.name,
                          homeTeam: game.homeTeam.name,
                          market: rec.market,
                          selection: rec.selection,
                          odds: rec.odds,
                          stakeAmount: rec.recommendedStake?.recommendedAmount ?? 10,
                          stakeUnits: rec.recommendedStake?.recommendedUnits,
                          modelProbability: rec.modelProbability,
                          edge: rec.edge,
                          confidence: rec.confidence,
                          recommendationId: rec.id,
                        });
                      } finally {
                        setPlacing(null);
                      }
                    }}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-brand/20 border border-brand/30 text-brand-light text-[10px] font-semibold hover:bg-brand/30 transition-colors disabled:opacity-50 flex-shrink-0"
                  >
                    <DollarSign className="w-3 h-3" />
                    {placing === rec.id ? 'Placing…' : 'Place Bet'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Active bets */}
      {activeBets.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Open Bets</p>
          {activeBets.map((bet) => (
            <div key={bet.id} className="rounded-lg border border-surface-4 bg-surface-2 p-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">{bet.market}</p>
                  <p className="text-sm font-semibold text-gray-200">{bet.selection}</p>
                  <p className="text-xs text-gray-600 mt-0.5">
                    ${bet.stakeAmount.toFixed(2)} @ {bet.odds > 0 ? '+' : ''}{bet.odds}
                  </p>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  {(['won', 'lost', 'push'] as const).map((result) => (
                    <button
                      key={result}
                      disabled={settling === bet.id}
                      onClick={async () => {
                        setSettling(bet.id);
                        try { await onSettleBet(bet.id, result); } finally { setSettling(null); }
                      }}
                      className={clsx(
                        'px-2 py-1 rounded text-[10px] font-semibold transition-colors disabled:opacity-50',
                        result === 'won' ? 'bg-win/15 text-win hover:bg-win/25' :
                        result === 'lost' ? 'bg-loss/15 text-loss hover:bg-loss/25' :
                        'bg-surface-3 text-gray-500 hover:bg-surface-4',
                      )}
                    >
                      {result}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Settled bets */}
      {settledBets.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Settled</p>
          {settledBets.map((bet) => (
            <div key={bet.id} className={clsx(
              'rounded-lg border p-3',
              bet.result === 'won' ? 'border-win/20 bg-win/5' :
              bet.result === 'lost' ? 'border-loss/20 bg-loss/5' :
              'border-surface-4 bg-surface-2',
            )}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-600 uppercase">{bet.market} · {bet.selection}</p>
                  <p className="text-xs text-gray-500 mt-0.5">${bet.stakeAmount.toFixed(2)} @ {bet.odds > 0 ? '+' : ''}{bet.odds}</p>
                </div>
                <div className="text-right">
                  <p className={clsx(
                    'text-xs font-bold capitalize',
                    bet.result === 'won' ? 'text-win' : bet.result === 'lost' ? 'text-loss' : 'text-gray-500',
                  )}>
                    {bet.result}
                  </p>
                  {bet.pnl !== undefined && (
                    <p className={clsx('text-xs tabular-nums', bet.pnl >= 0 ? 'text-win' : 'text-loss')}>
                      {bet.pnl >= 0 ? '+' : ''}${bet.pnl.toFixed(2)}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {recommendations.length === 0 && gameBets.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 gap-2 text-center">
          <DollarSign className="w-6 h-6 text-gray-700" />
          <p className="text-sm text-gray-600">No qualified picks for this game yet.</p>
          <p className="text-xs text-gray-700">Run analysis to generate recommendations.</p>
        </div>
      )}
    </div>
  );
}

function WNBAPropsTab({
  props,
  awayTeam,
  homeTeam,
  injuries,
}: {
  props: WNBAPlayerProjection[];
  awayTeam: { id: string; abbreviation: string };
  homeTeam: { id: string; abbreviation: string };
  injuries: WNBAInjuryReport[];
}) {
  if (props.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-2 text-center">
        <Activity className="w-6 h-6 text-gray-700" />
        <p className="text-sm text-gray-600">Props not available for this game.</p>
        <p className="text-xs text-gray-700">Check back closer to tip-off.</p>
      </div>
    );
  }

  const injuredIds = new Set(
    injuries
      .filter((i) => i.status === 'Out' || i.status === 'Questionable' || i.status === 'Doubtful')
      .map((i) => i.playerId),
  );

  const awayProps = props.filter((p) => {
    const inj = injuries.find((i) => i.playerId === p.playerId);
    return inj ? inj.teamId === awayTeam.id : true;
  }).slice(0, 8);

  const homeProps = props.filter((p) => {
    const inj = injuries.find((i) => i.playerId === p.playerId);
    return inj ? inj.teamId === homeTeam.id : true;
  }).slice(0, 8);

  const PropsTable = ({ teamAbbr, projections }: { teamAbbr: string; projections: WNBAPlayerProjection[] }) => (
    <div className="rounded-lg border border-surface-4 bg-surface-2 overflow-hidden">
      <div className="px-3 py-2 border-b border-surface-4 bg-surface-1/20">
        <span className="text-xs font-semibold text-gray-400">{teamAbbr}</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-surface-4 text-gray-600">
              <th className="text-left px-3 py-2 font-medium">Player</th>
              <th className="text-right px-2 py-2 font-medium">MIN</th>
              <th className="text-right px-2 py-2 font-medium">PTS</th>
              <th className="text-right px-2 py-2 font-medium">REB</th>
              <th className="text-right px-2 py-2 font-medium">AST</th>
              <th className="text-right px-2 py-2 font-medium">PRA</th>
              <th className="text-right px-3 py-2 font-medium">USG%</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-surface-4">
            {projections.map((p) => {
              const isInjured = injuredIds.has(p.playerId);
              return (
                <tr key={p.playerId} className={clsx('hover:bg-surface-1/20 transition-colors', isInjured && 'opacity-50')}>
                  <td className="px-3 py-2 text-gray-300 font-medium truncate max-w-[120px]">
                    {p.playerName}
                    {isInjured && <span className="ml-1 text-warn text-[9px]">⚠</span>}
                    {(p.blowoutRiskPct ?? 0) > 0.25 && <span className="ml-1 text-gray-600 text-[9px]" title="Blowout risk">BL</span>}
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums text-gray-400">{p.projectedMinutes?.toFixed(0) ?? '—'}</td>
                  <td className="px-2 py-2 text-right tabular-nums text-gray-300 font-medium">{p.projectedPoints?.toFixed(1) ?? '—'}</td>
                  <td className="px-2 py-2 text-right tabular-nums text-gray-400">{p.projectedRebounds?.toFixed(1) ?? '—'}</td>
                  <td className="px-2 py-2 text-right tabular-nums text-gray-400">{p.projectedAssists?.toFixed(1) ?? '—'}</td>
                  <td className="px-2 py-2 text-right tabular-nums text-warn font-medium">{p.projectedPra?.toFixed(1) ?? '—'}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-500">
                    {p.usageShare !== undefined ? `${(p.usageShare * 100).toFixed(0)}%` : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="space-y-4 max-w-2xl">
      {awayProps.length > 0 && <PropsTable teamAbbr={awayTeam.abbreviation} projections={awayProps} />}
      {homeProps.length > 0 && <PropsTable teamAbbr={homeTeam.abbreviation} projections={homeProps} />}
      {props.length > 0 && (
        <p className="text-xs text-gray-700">
          {props.length} player projection{props.length !== 1 ? 's' : ''} · Blowout risk shown as BL
        </p>
      )}
    </div>
  );
}

function WNBAInjuriesTab({
  injuries,
  awayTeam,
  homeTeam,
}: {
  injuries: WNBAInjuryReport[];
  awayTeam: { id: string; name: string; abbreviation: string };
  homeTeam: { id: string; name: string; abbreviation: string };
}) {
  const awayInjuries = injuries.filter((i) => i.teamId === awayTeam.id);
  const homeInjuries = injuries.filter((i) => i.teamId === homeTeam.id);

  if (awayInjuries.length === 0 && homeInjuries.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-2 text-center">
        <Users className="w-6 h-6 text-gray-700" />
        <p className="text-sm text-gray-600">No injury reports for this matchup.</p>
      </div>
    );
  }

  const statusColor = (status: string) =>
    status === 'Out' ? 'text-loss bg-loss/10 border-loss/30' :
    status === 'Questionable' ? 'text-warn bg-warn/10 border-warn/30' :
    status === 'Doubtful' ? 'text-warn/70 bg-warn/5 border-warn/20' :
    'text-gray-500 bg-surface-3 border-surface-4';

  const InjuryList = ({ teamName, teamInjuries }: { teamName: string; teamInjuries: WNBAInjuryReport[] }) => (
    <div className="rounded-lg border border-surface-4 bg-surface-2 overflow-hidden">
      <div className="px-3 py-2 border-b border-surface-4 bg-surface-1/20">
        <span className="text-xs font-semibold text-gray-400">{teamName}</span>
      </div>
      {teamInjuries.length === 0 ? (
        <p className="text-xs text-gray-600 px-3 py-3">No reports</p>
      ) : (
        <div className="divide-y divide-surface-4">
          {teamInjuries.map((inj, i) => (
            <div key={i} className="flex items-start justify-between gap-3 px-3 py-2.5">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-200 truncate">{inj.playerName}</p>
                <p className="text-xs text-gray-600 truncate">{inj.detail ?? 'Injury report'}</p>
              </div>
              <span className={clsx('text-[10px] font-bold px-1.5 py-0.5 rounded border flex-shrink-0', statusColor(inj.status))}>
                {inj.status}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-4 max-w-2xl">
      <InjuryList teamName={`${awayTeam.name} (Away)`} teamInjuries={awayInjuries} />
      <InjuryList teamName={`${homeTeam.name} (Home)`} teamInjuries={homeInjuries} />
    </div>
  );
}

// ── Decision Summary Panel ─────────────────────────────────────────────────────

const STATUS_STYLES: Record<string, { border: string; bg: string; badge: string; label: string }> = {
  STRONG_BET:   { border: 'border-win/40',    bg: 'bg-win/5',     badge: 'bg-win/20 text-win border-win/30',           label: 'STRONG BET' },
  QUALIFIED_BET:{ border: 'border-brand/30',  bg: 'bg-brand/5',   badge: 'bg-brand/20 text-brand-light border-brand/30', label: 'QUALIFIED' },
  LEAN:         { border: 'border-warn/30',   bg: 'bg-warn/5',    badge: 'bg-warn/20 text-warn border-warn/30',          label: 'LEAN' },
  NO_BET:       { border: 'border-surface-4', bg: 'bg-surface-2', badge: 'bg-surface-3 text-gray-400 border-surface-4',  label: 'NO BET' },
  INVALID:      { border: 'border-loss/20',   bg: 'bg-loss/5',    badge: 'bg-loss/20 text-loss border-loss/30',          label: 'BLOCKED' },
};

function pickBestCandidate(candidates: BetRecommendation[]): BetRecommendation | undefined {
  const p: Record<string, number> = { STRONG_BET: 0, QUALIFIED_BET: 1, LEAN: 2, NO_BET: 3, INVALID: 4 };
  return [...candidates].sort((a, b) => (p[a.decisionStatus] ?? 99) - (p[b.decisionStatus] ?? 99))[0];
}

function DecisionSummaryPanel({ candidates }: { candidates: BetRecommendation[] }) {
  if (candidates.length === 0) return null;
  const best = pickBestCandidate(candidates);
  if (!best) return null;
  const style = STATUS_STYLES[best.decisionStatus] ?? STATUS_STYLES.NO_BET;
  const nonActionableCodes = best.decisionReasonCodes?.filter(
    (c) => !best.trustReasonCodes?.includes(c),
  ) ?? [];

  return (
    <div className={clsx('mx-4 mt-3 rounded-lg border p-3 flex-shrink-0', style.border, style.bg)}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={clsx('text-[10px] font-bold px-1.5 py-0.5 rounded border', style.badge)}>
            {style.label}
          </span>
          <span className="text-xs text-gray-500">{best.market} · {best.selection}</span>
        </div>
        <div className="flex items-center gap-3 text-[10px] text-gray-600 flex-shrink-0">
          {best.edge !== undefined && (
            <span>Edge <span className="font-mono text-gray-400">{(best.edge * 100).toFixed(1)}%</span></span>
          )}
          {best.confidence !== undefined && (
            <span>Conf <span className="font-mono text-gray-400">{(best.confidence * 100).toFixed(0)}%</span></span>
          )}
        </div>
      </div>
      {best.decisionSummary && (
        <p className="text-xs text-gray-500 mt-2">{best.decisionSummary}</p>
      )}
      {nonActionableCodes.length > 0 && (
        <div className="flex gap-1 flex-wrap mt-2">
          {nonActionableCodes.map((code) => (
            <span key={code} className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-surface-3 border border-surface-4 text-gray-500">
              {code}
            </span>
          ))}
        </div>
      )}
      {best.reasonsFor.length > 0 && (
        <div className="mt-2 space-y-0.5">
          {best.reasonsFor.slice(0, 2).map((r, i) => (
            <p key={i} className="flex items-center gap-1.5 text-[10px] text-gray-500">
              <TrendingUp className="w-2.5 h-2.5 text-win flex-shrink-0" />{r.text}
            </p>
          ))}
        </div>
      )}
      {best.reasonsAgainst.length > 0 && (
        <div className="mt-1 space-y-0.5">
          {best.reasonsAgainst.slice(0, 2).map((r, i) => (
            <p key={i} className="flex items-center gap-1.5 text-[10px] text-gray-500">
              <TrendingDown className="w-2.5 h-2.5 text-loss flex-shrink-0" />{r.text}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

// ── WNBACanvas ────────────────────────────────────────────────────────────────

function WNBACanvas({ eventPk, onClose }: { eventPk: string; onClose: () => void }) {
  const [tab, setTab] = useState<WNBATab>('overview');
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeError, setAnalyzeError] = useState<string | null>(null);
  const [liveRecs, setLiveRecs] = useState<BetRecommendation[]>([]);

  const { data: context, isLoading } = useQuery({
    queryKey: ['wnba-game-canvas', eventPk],
    queryFn: () => wnbaApi.getGame(eventPk),
    staleTime: 30_000,
    enabled: !!eventPk,
  });

  const { data: recommendations = [], refetch: refetchRecs } = useQuery({
    queryKey: ['wnba-recs-canvas', eventPk],
    queryFn: () => wnbaApi.getRecommendations().then((recs) => recs.filter((r) => r.eventPk === eventPk)),
    staleTime: 120_000,
    enabled: !!eventPk,
    retry: false,
  });

  const { data: auditCandidates = [], refetch: refetchAudit } = useQuery({
    queryKey: ['wnba-audit-canvas', eventPk, context?.game?.gameDate],
    queryFn: () => advisorApi.getAuditCandidates(context!.game.gameDate, eventPk, 'wnba'),
    staleTime: 120_000,
    enabled: !!eventPk && !!context?.game?.gameDate,
    retry: false,
  });

  // Live game polling — 20s when game is in progress
  const { data: liveContext } = useQuery({
    queryKey: ['wnba-live-canvas', eventPk],
    queryFn: () => wnbaApi.getLiveGame(eventPk),
    refetchInterval: context?.game?.status?.state === 'in' ? 20_000 : false,
    enabled: !!eventPk && context?.game?.status?.state === 'in',
    retry: false,
  });

  // Player props
  const { data: props = [] } = useQuery<WNBAPlayerProjection[]>({
    queryKey: ['wnba-props-canvas', eventPk],
    queryFn: () => wnbaApi.getGameProps(eventPk),
    staleTime: 5 * 60_000,
    enabled: !!eventPk,
    retry: false,
  });

  // Injury report (keyed by game date so multiple games share cache)
  const { data: injuries = [] } = useQuery<WNBAInjuryReport[]>({
    queryKey: ['wnba-injuries-canvas', context?.game?.gameDate],
    queryFn: () => wnbaApi.getInjuries(context?.game?.gameDate),
    staleTime: 30 * 60_000,
    enabled: !!context?.game?.gameDate,
    retry: false,
  });

  // Open bets for this game from store
  const { bets: allBets, placeBet, settleBet } = useAppStore();
  const gameBets = allBets.filter((b) => b.eventPk === eventPk);

  async function handleAnalyze() {
    if (!eventPk) return;
    setAnalyzing(true);
    setAnalyzeError(null);
    try {
      const freshRecs = await wnbaApi.analyzeGame(eventPk);
      setLiveRecs(freshRecs);
      void refetchRecs();
      void refetchAudit();
    } catch (err) {
      setAnalyzeError(err instanceof Error ? err.message : 'Analysis failed. Please try again.');
    } finally {
      setAnalyzing(false);
    }
  }

  if (isLoading || !context) {
    return (
      <div className="flex flex-col h-full bg-surface-0">
        <CanvasHeader sport="wnba" title="Loading…" onClose={onClose} analyzing={false} onAnalyze={() => void 0} />
        <div className="flex-1 flex items-center justify-center">
          <RefreshCw className="w-5 h-5 text-gray-600 animate-spin" />
        </div>
      </div>
    );
  }

  const { game: baseGame, awayTeamMetrics, homeTeamMetrics, awayRestProfile, homeRestProfile, odds } = context;
  // Use live context when available for real-time score/clock
  const game = liveContext?.game ?? baseGame;
  const isPost = game.status.state === 'post';
  const isLive = game.status.state === 'in';
  const awayWins = isPost && game.awayScore !== undefined && game.homeScore !== undefined && game.awayScore > game.homeScore;
  const homeWins = isPost && game.awayScore !== undefined && game.homeScore !== undefined && game.homeScore > game.awayScore;
  const effectiveRecs = liveRecs.length > 0 ? liveRecs : recommendations;
  const qualifiedRecs = effectiveRecs.filter((r) => r.decisionStatus === 'STRONG_BET' || r.decisionStatus === 'QUALIFIED_BET');

  return (
    <div className="flex flex-col h-full overflow-hidden bg-surface-0">
      <CanvasHeader
        sport="wnba"
        title={`${game.awayTeam.abbreviation} @ ${game.homeTeam.abbreviation}`}
        subtitle={game.venue.name}
        status={game.status.state === 'in' ? 'Live' : game.status.state === 'post' ? 'Final' : game.status.detail}
        onClose={onClose}
        analyzing={analyzing}
        onAnalyze={handleAnalyze}
      />

      {/* Scoreboard */}
      <div className="px-4 py-2.5 border-b border-surface-4 flex-shrink-0 bg-surface-1/20">
        <div className="space-y-1">
          {[
            { team: game.awayTeam, score: game.awayScore, isHome: false, wins: awayWins },
            { team: game.homeTeam, score: game.homeScore, isHome: true, wins: homeWins },
          ].map(({ team, score, isHome, wins }) => (
            <div key={team.id} className="flex items-center justify-between">
              <div className="min-w-0">
                <span className={clsx('text-sm font-medium', wins ? 'text-gray-100' : 'text-gray-400')}>
                  {team.location} <span className="text-gray-300">{team.name.split(' ').pop()}</span>
                </span>
                <span className="ml-2 text-xs text-gray-600">{isHome ? 'Home' : 'Away'}</span>
              </div>
              {score !== undefined && (
                <span className={clsx('text-xl font-bold tabular-nums', wins ? 'text-gray-100' : 'text-gray-500')}>
                  {score}
                </span>
              )}
            </div>
          ))}
        </div>
        {isLive && (game.status.period || game.status.clock) && (
          <div className="flex items-center gap-1.5 mt-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-win animate-pulse flex-shrink-0" />
            <span className="text-[10px] font-semibold text-win">
              {game.status.period ? `Q${game.status.period}` : 'Live'}
              {game.status.clock ? ` — ${game.status.clock}` : ''}
            </span>
          </div>
        )}
      </div>

      {/* Tab bar */}
      <div className="flex items-center px-4 border-b border-surface-4 overflow-x-auto flex-shrink-0 bg-surface-1/10">
        {WNBA_TABS.map((t) => {
          const badge =
            t.key === 'recommendations' && qualifiedRecs.length > 0 ? qualifiedRecs.length :
            t.key === 'bets' && gameBets.length > 0 ? gameBets.length :
            t.key === 'injuries' && injuries.filter((i) => i.status === 'Out' || i.status === 'Questionable').length > 0
              ? injuries.filter((i) => i.status === 'Out' || i.status === 'Questionable').length :
            null;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={clsx(
                'flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium border-b-2 whitespace-nowrap transition-colors flex-shrink-0',
                tab === t.key
                  ? 'border-warn text-warn'
                  : 'border-transparent text-gray-500 hover:text-gray-300 hover:border-surface-4',
              )}
            >
              {t.label}
              {badge !== null && (
                <span className={clsx(
                  'min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold flex items-center justify-center',
                  t.key === 'injuries' ? 'bg-warn/20 text-warn' : 'bg-win/20 text-win',
                )}>
                  {badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {analyzeError && (
        <div className="flex items-center gap-2 mx-4 mt-2 px-3 py-2 rounded-md bg-loss/10 border border-loss/20 text-xs text-loss/80 flex-shrink-0">
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
          {analyzeError}
        </div>
      )}

      {auditCandidates.length > 0 && <DecisionSummaryPanel candidates={auditCandidates} />}

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto p-4">
        {tab === 'overview' && (
          <div className="space-y-4 max-w-xl">
            <div className="rounded-lg border border-surface-4 bg-surface-2 p-4 space-y-3">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Game Info</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><p className="text-gray-600 text-xs">Venue</p><p className="text-gray-300">{game.venue.name}</p></div>
                <div><p className="text-gray-600 text-xs">Surface</p><p className="text-gray-300">{game.venue.indoor ? 'Indoor' : 'Outdoor'}</p></div>
                {game.gameTime && (
                  <div>
                    <p className="text-gray-600 text-xs">Tip-off</p>
                    <p className="text-gray-300">{new Date(game.gameTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                  </div>
                )}
                <div>
                  <p className="text-gray-600 text-xs">Data Quality</p>
                  <p className="text-gray-300">{(context.dataQuality.overallScore * 100).toFixed(0)}%</p>
                </div>
              </div>
            </div>
            {context.dataQuality.warnings.length > 0 && (
              <div className="rounded-lg border border-warn/20 bg-warn/5 p-3">
                <p className="text-xs font-semibold text-warn mb-1.5">Data Warnings</p>
                <ul className="space-y-0.5">
                  {context.dataQuality.warnings.map((w, i) => (
                    <li key={i} className="text-xs text-warn/80">• {w}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {tab === 'markets' && (
          <div className="space-y-3 max-w-lg">
            {!odds ? (
              <div className="text-center py-8 text-gray-600 text-sm">No odds data available</div>
            ) : (
              <>
                {[
                  { label: 'Moneyline', rows: [
                    { team: game.awayTeam.abbreviation, value: formatOddsAmerican(odds.awayMoneyline) },
                    { team: game.homeTeam.abbreviation, value: formatOddsAmerican(odds.homeMoneyline) },
                  ]},
                  odds.spreadLine !== undefined ? { label: 'Spread', rows: [
                    { team: game.awayTeam.abbreviation, value: `${formatOddsAmerican(odds.awaySpread)} (${formatOddsAmerican(odds.spreadLine)})` },
                    { team: game.homeTeam.abbreviation, value: formatOddsAmerican(odds.homeSpread) },
                  ]} : null,
                  odds.total !== undefined ? { label: 'Total', rows: [
                    { team: 'Over', value: `${odds.total} (${formatOddsAmerican(odds.overOdds)})` },
                    { team: 'Under', value: `${odds.total} (${formatOddsAmerican(odds.underOdds)})` },
                  ]} : null,
                ].filter(Boolean).map((market) => (
                  <div key={market!.label} className="rounded-lg border border-surface-4 bg-surface-2 overflow-hidden">
                    <div className="px-4 py-2 border-b border-surface-4">
                      <span className="text-xs font-semibold text-gray-400">{market!.label}</span>
                    </div>
                    <div className="divide-y divide-surface-4">
                      {market!.rows.map((row) => (
                        <div key={row.team} className="flex items-center justify-between px-4 py-2.5">
                          <span className="text-sm text-gray-300">{row.team}</span>
                          <span className="text-sm font-medium text-gray-200 tabular-nums">{row.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                {odds.source && (
                  <p className="text-xs text-gray-600">
                    Source: {odds.source}{odds.fetchedAt ? ` · ${new Date(odds.fetchedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''}
                  </p>
                )}
              </>
            )}
          </div>
        )}

        {tab === 'metrics' && awayTeamMetrics && homeTeamMetrics && (
          <div className="max-w-lg">
            <TeamMetricsPanel
              awayTeam={game.awayTeam}
              homeTeam={game.homeTeam}
              awayTeamMetrics={awayTeamMetrics}
              homeTeamMetrics={homeTeamMetrics}
            />
          </div>
        )}

        {tab === 'rest' && awayRestProfile && homeRestProfile && (
          <div className="max-w-lg">
            <RestAnalysisPanel
              awayTeam={game.awayTeam}
              homeTeam={game.homeTeam}
              awayRest={awayRestProfile}
              homeRest={homeRestProfile}
            />
          </div>
        )}

        {tab === 'form' && awayTeamMetrics && homeTeamMetrics && (
          <div className="max-w-lg">
            <FormTrendPanel
              awayTeam={game.awayTeam}
              homeTeam={game.homeTeam}
              awayTeamMetrics={awayTeamMetrics}
              homeTeamMetrics={homeTeamMetrics}
            />
          </div>
        )}

        {tab === 'recommendations' && (
          <div className="space-y-3 max-w-2xl">
            {effectiveRecs.length === 0 ? (
              auditCandidates.length > 0 ? (
                <div className="text-center py-6 text-gray-600 text-xs">
                  Analysis complete — no qualified picks for this game.
                </div>
              ) : (
                <div className="text-center py-8 text-gray-600 text-sm">
                  No recommendations yet. Click Analyze to run the agent pipeline.
                </div>
              )
            ) : (
              effectiveRecs.map((rec) => <WNBARecCard key={rec.id} rec={rec} />)
            )}
          </div>
        )}

        {tab === 'evidence' && (
          <WNBAEvidenceTab recommendations={auditCandidates.length > 0 ? auditCandidates : effectiveRecs} />
        )}

        {tab === 'bets' && (
          <WNBABetsTab
            game={game}
            recommendations={qualifiedRecs}
            gameBets={gameBets}
            onPlaceBet={placeBet}
            onSettleBet={settleBet}
          />
        )}

        {tab === 'props' && (
          <WNBAPropsTab
            props={props}
            awayTeam={game.awayTeam}
            homeTeam={game.homeTeam}
            injuries={injuries}
          />
        )}

        {tab === 'injuries' && (
          <WNBAInjuriesTab
            injuries={injuries}
            awayTeam={game.awayTeam}
            homeTeam={game.homeTeam}
          />
        )}
      </div>
    </div>
  );
}

// ── Shared canvas header ───────────────────────────────────────────────────────

const SPORT_META = {
  mlb:  { tag: 'MLB',  color: 'text-brand',       bg: 'bg-brand/10 border-brand/20' },
  wnba: { tag: 'WNBA', color: 'text-warn',         bg: 'bg-warn/10 border-warn/20' },
  nba:  { tag: 'NBA',  color: 'text-emerald-400',  bg: 'bg-emerald-500/10 border-emerald-500/20' },
};

interface CanvasHeaderProps {
  sport: keyof typeof SPORT_META;
  title: string;
  subtitle?: string;
  status?: string;
  onClose: () => void;
  analyzing: boolean;
  onAnalyze: () => void;
}

function CanvasHeader({ sport, title, subtitle, status, onClose, analyzing, onAnalyze }: CanvasHeaderProps) {
  const meta = SPORT_META[sport];
  const isLive = status === 'Live' || status === 'In Progress';

  return (
    <div className="flex items-center gap-3 px-3 py-2.5 border-b border-surface-4 bg-surface-1/30 flex-shrink-0">
      {/* Sport badge */}
      <span className={clsx('text-[10px] font-bold px-1.5 py-0.5 rounded border flex-shrink-0', meta.bg, meta.color)}>
        {meta.tag}
      </span>

      {/* Title */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <p className="text-sm font-semibold text-gray-100 truncate leading-tight">{title}</p>
          {isLive && (
            <span className="flex items-center gap-1 text-[10px] text-win font-semibold flex-shrink-0">
              <span className="w-1.5 h-1.5 rounded-full bg-win animate-pulse" />
              Live
            </span>
          )}
          {status && !isLive && status !== 'Live' && (
            <span className="text-[10px] text-gray-600 flex-shrink-0">{status}</span>
          )}
        </div>
        {subtitle && <p className="text-[10px] text-gray-600 truncate leading-tight">{subtitle}</p>}
      </div>

      {/* Analyze button */}
      <button
        onClick={onAnalyze}
        disabled={analyzing}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-brand/15 border border-brand/30 text-brand-light text-[10px] font-semibold hover:bg-brand/25 transition-colors disabled:opacity-50 flex-shrink-0"
      >
        <Zap className={clsx('w-3 h-3', analyzing && 'animate-pulse')} />
        {analyzing ? 'Analyzing…' : 'Analyze'}
      </button>

      {/* Close button */}
      <button
        onClick={onClose}
        className="flex items-center justify-center w-7 h-7 rounded-md text-gray-500 hover:text-gray-200 hover:bg-surface-3 transition-colors flex-shrink-0"
        title="Close game canvas"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

// ── GameIntelligenceCanvas ────────────────────────────────────────────────────
// Root export — reads OS store and routes to the appropriate sport canvas.

// ── NBACanvas ─────────────────────────────────────────────────────────────────

type NBATab = WNBATab; // same tab set + load management shown inside 'rest'

function NBACanvas({ eventPk, onClose }: { eventPk: string; onClose: () => void }) {
  const [tab, setTab] = useState<NBATab>('overview');
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeError, setAnalyzeError] = useState<string | null>(null);
  const [liveRecs, setLiveRecs] = useState<BetRecommendation[]>([]);

  const { data: context, isLoading } = useQuery({
    queryKey: ['nba-game-canvas', eventPk],
    queryFn: () => nbaApi.getGame(eventPk),
    staleTime: 30_000,
    enabled: !!eventPk,
  });

  const { data: recommendations = [], refetch: refetchRecs } = useQuery({
    queryKey: ['nba-recs-canvas', eventPk],
    queryFn: () => nbaApi.getRecommendations().then((recs) => recs.filter((r) => r.eventPk === eventPk)),
    staleTime: 120_000,
    enabled: !!eventPk,
    retry: false,
  });

  const { data: auditCandidates = [], refetch: refetchAudit } = useQuery({
    queryKey: ['nba-audit-canvas', eventPk, context?.game?.gameDate],
    queryFn: () => advisorApi.getAuditCandidates(context!.game.gameDate, eventPk, 'nba'),
    staleTime: 120_000,
    enabled: !!eventPk && !!context?.game?.gameDate,
    retry: false,
  });

  const { data: liveContext } = useQuery({
    queryKey: ['nba-live-canvas', eventPk],
    queryFn: () => nbaApi.getLiveGame(eventPk),
    refetchInterval: context?.game?.status?.state === 'in' ? 20_000 : false,
    enabled: !!eventPk && context?.game?.status?.state === 'in',
    retry: false,
  });

  const { data: props = [] } = useQuery<NBAPlayerProjection[]>({
    queryKey: ['nba-props-canvas', eventPk],
    queryFn: () => nbaApi.getGameProps(eventPk),
    staleTime: 5 * 60_000,
    enabled: !!eventPk,
    retry: false,
  });

  const { data: injuries = [] } = useQuery<NBAInjuryReport[]>({
    queryKey: ['nba-injuries-canvas', context?.game?.gameDate],
    queryFn: () => nbaApi.getInjuries(context?.game?.gameDate),
    staleTime: 30 * 60_000,
    enabled: !!context?.game?.gameDate,
    retry: false,
  });

  const { bets: allBets, placeBet, settleBet } = useAppStore();
  const gameBets = allBets.filter((b) => b.eventPk === eventPk);

  async function handleAnalyze() {
    if (!eventPk) return;
    setAnalyzing(true);
    setAnalyzeError(null);
    try {
      const freshRecs = await nbaApi.analyzeGame(eventPk);
      setLiveRecs(freshRecs);
      void refetchRecs();
      void refetchAudit();
    } catch (err) {
      setAnalyzeError(err instanceof Error ? err.message : 'Analysis failed. Please try again.');
    } finally {
      setAnalyzing(false);
    }
  }

  if (isLoading || !context) {
    return (
      <div className="flex flex-col h-full bg-surface-0">
        <CanvasHeader sport="nba" title="Loading…" onClose={onClose} analyzing={false} onAnalyze={() => void 0} />
        <div className="flex-1 flex items-center justify-center">
          <RefreshCw className="w-5 h-5 text-gray-600 animate-spin" />
        </div>
      </div>
    );
  }

  const { game: baseGame, awayTeamMetrics, homeTeamMetrics, awayRestProfile, homeRestProfile, odds } = context;
  const game = liveContext?.game ?? baseGame;
  const isPost = game.status.state === 'post';
  const isLive = game.status.state === 'in';
  const awayWins = isPost && game.awayScore !== undefined && game.homeScore !== undefined && game.awayScore > game.homeScore;
  const homeWins = isPost && game.awayScore !== undefined && game.homeScore !== undefined && game.homeScore > game.awayScore;
  const effectiveRecs = liveRecs.length > 0 ? liveRecs : recommendations;
  const qualifiedRecs = effectiveRecs.filter((r) => r.decisionStatus === 'STRONG_BET' || r.decisionStatus === 'QUALIFIED_BET');

  const awayLoadManaged = awayRestProfile?.hasLoadManagementRisk;
  const homeLoadManaged = homeRestProfile?.hasLoadManagementRisk;

  return (
    <div className="flex flex-col h-full overflow-hidden bg-surface-0">
      <CanvasHeader
        sport="nba"
        title={`${game.awayTeam.abbreviation} @ ${game.homeTeam.abbreviation}`}
        subtitle={game.venue?.name}
        status={game.status.state === 'in' ? 'Live' : game.status.state === 'post' ? 'Final' : game.status.detail}
        onClose={onClose}
        analyzing={analyzing}
        onAnalyze={handleAnalyze}
      />

      {/* Scoreboard + playoff series badge */}
      <div className="px-4 py-2.5 border-b border-surface-4 flex-shrink-0 bg-surface-1/20">
        {game.isPlayoff && (
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/20 text-emerald-400">
              PLAYOFFS{game.seriesGameNumber ? ` · Game ${game.seriesGameNumber}` : ''}
            </span>
            {game.awaySeriesWins !== undefined && game.homeSeriesWins !== undefined && (
              <span className="text-[10px] text-gray-500">
                Series: {game.awayTeam.abbreviation} {game.awaySeriesWins}–{game.homeSeriesWins} {game.homeTeam.abbreviation}
              </span>
            )}
          </div>
        )}
        <div className="space-y-1">
          {[
            { team: game.awayTeam, score: game.awayScore, isHome: false, wins: awayWins, loadManaged: awayLoadManaged, names: awayRestProfile?.loadManagedPlayerNames },
            { team: game.homeTeam, score: game.homeScore, isHome: true, wins: homeWins, loadManaged: homeLoadManaged, names: homeRestProfile?.loadManagedPlayerNames },
          ].map(({ team, score, isHome, wins, loadManaged, names }) => (
            <div key={team.id} className="flex items-center justify-between">
              <div className="min-w-0 flex items-center gap-2">
                <span className={clsx('text-sm font-medium', wins ? 'text-gray-100' : 'text-gray-400')}>
                  {team.location} <span className="text-gray-300">{team.name.split(' ').pop()}</span>
                </span>
                <span className="text-xs text-gray-600">{isHome ? 'Home' : 'Away'}</span>
                {loadManaged && (
                  <span className="text-[9px] font-bold px-1 py-0.5 rounded bg-orange-500/15 border border-orange-500/25 text-orange-400" title={names?.join(', ')}>
                    LM
                  </span>
                )}
              </div>
              {score !== undefined && (
                <span className={clsx('text-xl font-bold tabular-nums', wins ? 'text-gray-100' : 'text-gray-500')}>
                  {score}
                </span>
              )}
            </div>
          ))}
        </div>
        {isLive && (game.status.period || game.status.clock) && (
          <div className="flex items-center gap-1.5 mt-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-win animate-pulse flex-shrink-0" />
            <span className="text-[10px] font-semibold text-win">
              {game.status.period ? `Q${game.status.period}` : 'Live'}
              {game.status.clock ? ` — ${game.status.clock}` : ''}
            </span>
          </div>
        )}
      </div>

      {/* Tab bar */}
      <div className="flex items-center px-4 border-b border-surface-4 overflow-x-auto flex-shrink-0 bg-surface-1/10">
        {WNBA_TABS.map((t) => {
          const badge =
            t.key === 'recommendations' && qualifiedRecs.length > 0 ? qualifiedRecs.length :
            t.key === 'bets' && gameBets.length > 0 ? gameBets.length :
            t.key === 'injuries' && injuries.filter((i) => i.status === 'Out' || i.status === 'Questionable').length > 0
              ? injuries.filter((i) => i.status === 'Out' || i.status === 'Questionable').length :
            null;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={clsx(
                'flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium border-b-2 whitespace-nowrap transition-colors flex-shrink-0',
                tab === t.key
                  ? 'border-emerald-400 text-emerald-400'
                  : 'border-transparent text-gray-500 hover:text-gray-300 hover:border-surface-4',
              )}
            >
              {t.label}
              {badge !== null && (
                <span className={clsx(
                  'min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold flex items-center justify-center',
                  t.key === 'injuries' ? 'bg-warn/20 text-warn' : 'bg-win/20 text-win',
                )}>
                  {badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {analyzeError && (
        <div className="flex items-center gap-2 mx-4 mt-2 px-3 py-2 rounded-md bg-loss/10 border border-loss/20 text-xs text-loss/80 flex-shrink-0">
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
          {analyzeError}
        </div>
      )}

      {auditCandidates.length > 0 && <DecisionSummaryPanel candidates={auditCandidates} />}

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto p-4">
        {tab === 'overview' && (
          <div className="space-y-4 max-w-xl">
            <div className="rounded-lg border border-surface-4 bg-surface-2 p-4 space-y-3">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Game Info</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><p className="text-gray-600 text-xs">Venue</p><p className="text-gray-300">{game.venue?.name ?? '—'}</p></div>
                <div><p className="text-gray-600 text-xs">Surface</p><p className="text-gray-300">Indoor</p></div>
                {game.gameTime && (
                  <div>
                    <p className="text-gray-600 text-xs">Tip-off</p>
                    <p className="text-gray-300">{new Date(game.gameTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                  </div>
                )}
                <div>
                  <p className="text-gray-600 text-xs">Data Quality</p>
                  <p className="text-gray-300">{(context.dataQuality.overallScore * 100).toFixed(0)}%</p>
                </div>
              </div>
            </div>
            {(awayLoadManaged || homeLoadManaged) && (
              <div className="rounded-lg border border-orange-500/20 bg-orange-500/5 p-3">
                <p className="text-xs font-semibold text-orange-400 mb-1.5">Load Management Alert</p>
                <ul className="space-y-0.5">
                  {awayLoadManaged && awayRestProfile?.loadManagedPlayerNames && (
                    <li className="text-xs text-orange-300/80">• {game.awayTeam.abbreviation}: {awayRestProfile.loadManagedPlayerNames.join(', ')}</li>
                  )}
                  {homeLoadManaged && homeRestProfile?.loadManagedPlayerNames && (
                    <li className="text-xs text-orange-300/80">• {game.homeTeam.abbreviation}: {homeRestProfile.loadManagedPlayerNames.join(', ')}</li>
                  )}
                </ul>
              </div>
            )}
            {context.dataQuality.warnings.length > 0 && (
              <div className="rounded-lg border border-warn/20 bg-warn/5 p-3">
                <p className="text-xs font-semibold text-warn mb-1.5">Data Warnings</p>
                <ul className="space-y-0.5">
                  {context.dataQuality.warnings.map((w, i) => (
                    <li key={i} className="text-xs text-warn/80">• {w}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {tab === 'markets' && (
          <div className="space-y-3 max-w-lg">
            {!odds ? (
              <div className="text-center py-8 text-gray-600 text-sm">No odds data available</div>
            ) : (
              <>
                {[
                  { label: 'Moneyline', rows: [
                    { team: game.awayTeam.abbreviation, value: formatOddsAmerican(odds.awayMoneyline) },
                    { team: game.homeTeam.abbreviation, value: formatOddsAmerican(odds.homeMoneyline) },
                  ]},
                  odds.spreadLine !== undefined ? { label: 'Spread', rows: [
                    { team: game.awayTeam.abbreviation, value: `${formatOddsAmerican(odds.awaySpread)} (${formatOddsAmerican(odds.spreadLine)})` },
                    { team: game.homeTeam.abbreviation, value: formatOddsAmerican(odds.homeSpread) },
                  ]} : null,
                  odds.total !== undefined ? { label: 'Total', rows: [
                    { team: 'Over', value: `${odds.total} (${formatOddsAmerican(odds.overOdds)})` },
                    { team: 'Under', value: `${odds.total} (${formatOddsAmerican(odds.underOdds)})` },
                  ]} : null,
                ].filter(Boolean).map((market) => (
                  <div key={market!.label} className="rounded-lg border border-surface-4 bg-surface-2 overflow-hidden">
                    <div className="px-4 py-2 border-b border-surface-4">
                      <span className="text-xs font-semibold text-gray-400">{market!.label}</span>
                    </div>
                    <div className="divide-y divide-surface-4">
                      {market!.rows.map((row) => (
                        <div key={row.team} className="flex items-center justify-between px-4 py-2.5">
                          <span className="text-sm text-gray-300">{row.team}</span>
                          <span className="text-sm font-medium text-gray-200 tabular-nums">{row.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                {odds.source && (
                  <p className="text-xs text-gray-600">
                    Source: {odds.source}{odds.fetchedAt ? ` · ${new Date(odds.fetchedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''}
                  </p>
                )}
              </>
            )}
          </div>
        )}

        {tab === 'metrics' && awayTeamMetrics && homeTeamMetrics && (
          <div className="max-w-lg">
            <TeamMetricsPanel
              awayTeam={game.awayTeam}
              homeTeam={game.homeTeam}
              awayTeamMetrics={awayTeamMetrics}
              homeTeamMetrics={homeTeamMetrics}
            />
          </div>
        )}

        {tab === 'rest' && awayRestProfile && homeRestProfile && (
          <div className="max-w-lg">
            <RestAnalysisPanel
              awayTeam={game.awayTeam}
              homeTeam={game.homeTeam}
              awayRest={awayRestProfile}
              homeRest={homeRestProfile}
            />
          </div>
        )}

        {tab === 'form' && awayTeamMetrics && homeTeamMetrics && (
          <div className="max-w-lg">
            <FormTrendPanel
              awayTeam={game.awayTeam}
              homeTeam={game.homeTeam}
              awayTeamMetrics={awayTeamMetrics}
              homeTeamMetrics={homeTeamMetrics}
            />
          </div>
        )}

        {tab === 'recommendations' && (
          <div className="space-y-3 max-w-2xl">
            {effectiveRecs.length === 0 ? (
              auditCandidates.length > 0 ? (
                <div className="text-center py-6 text-gray-600 text-xs">
                  Analysis complete — no qualified picks for this game.
                </div>
              ) : (
                <div className="text-center py-8 text-gray-600 text-sm">
                  No recommendations yet. Click Analyze to run the agent pipeline.
                </div>
              )
            ) : (
              effectiveRecs.map((rec) => <WNBARecCard key={rec.id} rec={rec} />)
            )}
          </div>
        )}

        {tab === 'evidence' && (
          <WNBAEvidenceTab recommendations={auditCandidates.length > 0 ? auditCandidates : effectiveRecs} />
        )}

        {tab === 'bets' && (
          <WNBABetsTab
            game={game}
            recommendations={qualifiedRecs}
            gameBets={gameBets}
            onPlaceBet={placeBet}
            onSettleBet={settleBet}
          />
        )}

        {tab === 'props' && (
          <NBAPropsTab
            props={props}
            awayTeam={game.awayTeam}
            homeTeam={game.homeTeam}
            injuries={injuries}
          />
        )}

        {tab === 'injuries' && (
          <NBAInjuriesTab
            injuries={injuries}
            awayTeam={game.awayTeam}
            homeTeam={game.homeTeam}
          />
        )}
      </div>
    </div>
  );
}

// ── NBA props tab ─────────────────────────────────────────────────────────────

function NBAPropsTab({ props, awayTeam, homeTeam, injuries }: {
  props: NBAPlayerProjection[];
  awayTeam: { id: string; abbreviation: string };
  homeTeam: { id: string; abbreviation: string };
  injuries: NBAInjuryReport[];
}) {
  const injuryMap = new Map(injuries.map((i) => [i.playerId, i]));
  const sortedProps = [...props].sort((a, b) => (b.projectedPoints ?? 0) - (a.projectedPoints ?? 0));

  const statCols: { key: keyof NBAPlayerProjection; label: string }[] = [
    { key: 'projectedPoints', label: 'PTS' },
    { key: 'projectedRebounds', label: 'REB' },
    { key: 'projectedAssists', label: 'AST' },
    { key: 'projectedThrees', label: '3PM' },
    { key: 'projectedBlocks', label: 'BLK' },
    { key: 'projectedSteals', label: 'STL' },
    { key: 'projectedPRA', label: 'PRA' },
  ];

  return (
    <div className="space-y-2 max-w-2xl">
      {sortedProps.length === 0 ? (
        <div className="text-center py-8 text-gray-600 text-sm">No player projections available</div>
      ) : (
        sortedProps.map((p) => {
          const inj = injuryMap.get(p.playerId);
          const teamAbbr = p.teamId === awayTeam.id ? awayTeam.abbreviation : homeTeam.abbreviation;
          return (
            <div key={p.playerId} className="rounded border border-surface-4 bg-surface-2 px-3 py-2.5">
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-200">{p.playerName}</span>
                  <span className="text-[10px] text-gray-600">{teamAbbr}</span>
                  {p.isLoadManagement && (
                    <span className="text-[9px] font-bold px-1 py-0.5 rounded bg-orange-500/15 border border-orange-500/25 text-orange-400">LM</span>
                  )}
                  {inj && !inj.isLoadManagement && (
                    <span className={clsx(
                      'text-[9px] font-bold px-1 py-0.5 rounded border',
                      inj.status === 'Out' ? 'bg-loss/15 border-loss/30 text-loss' : 'bg-warn/15 border-warn/30 text-warn',
                    )}>
                      {inj.status}
                    </span>
                  )}
                </div>
                {p.projectedMinutes !== undefined && (
                  <span className="text-[10px] text-gray-600 tabular-nums">{p.projectedMinutes.toFixed(0)} min</span>
                )}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {statCols.map(({ key, label }) => {
                  const val = p[key] as number | undefined;
                  return (
                    <div key={label} className="text-center">
                      <p className="text-[9px] text-gray-600 uppercase tracking-wide">{label}</p>
                      <p className={clsx('text-xs font-medium tabular-nums', val !== undefined ? 'text-gray-300' : 'text-gray-700')}>
                        {val !== undefined ? val.toFixed(1) : '—'}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

// ── NBA injuries tab ──────────────────────────────────────────────────────────

function NBAInjuriesTab({ injuries, awayTeam, homeTeam }: {
  injuries: NBAInjuryReport[];
  awayTeam: { id: string; name: string; abbreviation: string };
  homeTeam: { id: string; name: string; abbreviation: string };
}) {
  const loadManaged = injuries.filter((i) => i.isLoadManagement);
  const injured = injuries.filter((i) => !i.isLoadManagement && (i.status === 'Out' || i.status === 'Questionable' || i.status === 'Doubtful'));

  return (
    <div className="space-y-3 max-w-lg">
      {loadManaged.length > 0 && (
        <div className="rounded-lg border border-orange-500/20 bg-orange-500/5 overflow-hidden">
          <div className="px-4 py-2 border-b border-orange-500/15">
            <span className="text-xs font-semibold text-orange-400">Load Management</span>
          </div>
          <div className="divide-y divide-surface-4">
            {loadManaged.map((i) => (
              <div key={i.playerId} className="flex items-center justify-between px-4 py-2">
                <div>
                  <p className="text-sm text-gray-300">{i.playerName}</p>
                  <p className="text-xs text-gray-600">{i.teamId === awayTeam.id ? awayTeam.abbreviation : homeTeam.abbreviation}</p>
                </div>
                <span className="text-[10px] font-bold text-orange-400">REST</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {injured.length > 0 && (
        <div className="rounded-lg border border-surface-4 bg-surface-2 overflow-hidden">
          <div className="px-4 py-2 border-b border-surface-4">
            <span className="text-xs font-semibold text-gray-400">Injury Report</span>
          </div>
          <div className="divide-y divide-surface-4">
            {injured.map((i) => (
              <div key={i.playerId} className="flex items-center justify-between px-4 py-2">
                <div>
                  <p className="text-sm text-gray-300">{i.playerName}</p>
                  <p className="text-xs text-gray-600">{i.teamId === awayTeam.id ? awayTeam.abbreviation : homeTeam.abbreviation}{i.detail ? ` · ${i.detail}` : ''}</p>
                </div>
                <span className={clsx(
                  'text-[10px] font-bold px-1.5 py-0.5 rounded border',
                  i.status === 'Out' ? 'bg-loss/15 border-loss/30 text-loss' : 'bg-warn/15 border-warn/30 text-warn',
                )}>
                  {i.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      {loadManaged.length === 0 && injured.length === 0 && (
        <div className="text-center py-8 text-gray-600 text-sm">No injury or load management concerns</div>
      )}
    </div>
  );
}

export function GameIntelligenceCanvas() {
  const { activeGameId, activeGameSport, closeGameCanvas } = useOSStore();

  if (!activeGameId || !activeGameSport) return null;

  if (activeGameSport === 'mlb') {
    return <MLBCanvas gamePk={Number(activeGameId)} onClose={closeGameCanvas} />;
  }

  if (activeGameSport === 'wnba') {
    return <WNBACanvas eventPk={activeGameId} onClose={closeGameCanvas} />;
  }

  if (activeGameSport === 'nba') {
    return <NBACanvas eventPk={activeGameId} onClose={closeGameCanvas} />;
  }

  return null;
}
